import React, { useState } from 'react';
import { NeonText, CyberButton, GlowingBorder } from './CyberEffects';
import {
  upgradeToEmailAuth,
  loginWithEmail,
  registerWithEmail,
  logout,
  auth,
  isEmailUser,
} from '../firebase';
import './AuthModal.css';

function AuthModal({ isOpen, onClose, onAuthChange }) {
  const [mode, setMode] = useState('menu'); // menu, login, register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const currentUser = auth.currentUser;
  const isLoggedIn = isEmailUser();

  const handleClose = () => {
    setMode('menu');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      onAuthChange?.();
      handleClose();
    } catch (e) {
      setError('ログアウトに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    setLoading(true);
    try {
      // 匿名ユーザーの場合はアップグレード、そうでなければ新規登録
      if (currentUser && currentUser.isAnonymous) {
        await upgradeToEmailAuth(email, password);
      } else {
        await registerWithEmail(email, password);
      }
      onAuthChange?.();
      handleClose();
    } catch (e) {
      console.error('Registration error:', e);
      if (e.code === 'auth/email-already-in-use') {
        setError('このメールアドレスは既に使用されています');
      } else if (e.code === 'auth/invalid-email') {
        setError('メールアドレスの形式が正しくありません');
      } else if (e.code === 'auth/credential-already-in-use') {
        setError('この認証情報は既に他のアカウントで使用されています');
      } else {
        setError('登録に失敗しました: ' + e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    setLoading(true);
    try {
      await loginWithEmail(email, password);
      onAuthChange?.();
      handleClose();
    } catch (e) {
      console.error('Login error:', e);
      if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('メールアドレスまたはパスワードが正しくありません');
      } else if (e.code === 'auth/invalid-email') {
        setError('メールアドレスの形式が正しくありません');
      } else {
        setError('ログインに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  // ログイン済みの場合
  if (isLoggedIn) {
    return (
      <div className="auth-overlay" onClick={handleClose}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <GlowingBorder color="blue" className="auth-content">
            <h2 className="auth-title">
              <NeonText color="blue" size="lg">アカウント</NeonText>
            </h2>

            <div className="auth-info">
              <p className="auth-email">{currentUser?.email}</p>
              <p className="auth-status">ログイン中</p>
            </div>

            <div className="auth-actions">
              <CyberButton
                variant="secondary"
                className="auth-btn"
                onClick={handleLogout}
                disabled={loading}
              >
                {loading ? 'ログアウト中...' : 'ログアウト'}
              </CyberButton>
              <button className="auth-close-btn" onClick={handleClose}>
                閉じる
              </button>
            </div>
          </GlowingBorder>
        </div>
      </div>
    );
  }

  // メニュー画面
  if (mode === 'menu') {
    return (
      <div className="auth-overlay" onClick={handleClose}>
        <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
          <GlowingBorder color="accent" className="auth-content">
            <h2 className="auth-title">
              <NeonText color="accent" size="lg">アカウント</NeonText>
            </h2>

            <div className="auth-benefits">
              <p className="auth-benefits-title">アカウント登録のメリット</p>
              <ul className="auth-benefits-list">
                <li>📝 プレイ履歴を保存</li>
                <li>🔄 同じ問題が出にくくなる</li>
                <li>📊 成績を記録・確認</li>
              </ul>
            </div>

            <div className="auth-menu-buttons">
              <CyberButton
                variant="accent"
                className="auth-btn"
                onClick={() => setMode('register')}
              >
                新規登録
              </CyberButton>
              <CyberButton
                variant="primary"
                className="auth-btn"
                onClick={() => setMode('login')}
              >
                ログイン
              </CyberButton>
              <button className="auth-close-btn" onClick={handleClose}>
                閉じる
              </button>
            </div>
          </GlowingBorder>
        </div>
      </div>
    );
  }

  // 登録/ログインフォーム
  return (
    <div className="auth-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <GlowingBorder color={mode === 'register' ? 'accent' : 'blue'} className="auth-content">
          <button className="auth-back-btn" onClick={() => setMode('menu')}>
            ← 戻る
          </button>

          <h2 className="auth-title">
            <NeonText color={mode === 'register' ? 'accent' : 'blue'} size="lg">
              {mode === 'register' ? '新規登録' : 'ログイン'}
            </NeonText>
          </h2>

          <form onSubmit={mode === 'register' ? handleRegister : handleLogin} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">メールアドレス</label>
              <input
                type="email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="auth-field">
              <label className="auth-label">パスワード</label>
              <input
                type="password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6文字以上"
                disabled={loading}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              />
            </div>

            {mode === 'register' && (
              <div className="auth-field">
                <label className="auth-label">パスワード（確認）</label>
                <input
                  type="password"
                  className="auth-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="もう一度入力"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            )}

            {error && <p className="auth-error">{error}</p>}

            <CyberButton
              type="submit"
              variant={mode === 'register' ? 'accent' : 'primary'}
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading
                ? (mode === 'register' ? '登録中...' : 'ログイン中...')
                : (mode === 'register' ? '登録する' : 'ログイン')
              }
            </CyberButton>
          </form>
        </GlowingBorder>
      </div>
    </div>
  );
}

export default AuthModal;
